import { ScopeContext } from '@sentry/core';
import * as Sentry from '@sentry/core';

export type SentryAttachment = {
    file: File;
    contentType?: string;
};

export async function getFileAsUint8Array(file: File): Promise<Uint8Array> {
    if (typeof file.arrayBuffer === 'function') {
        const arrayBuffer = await file.arrayBuffer();
        return new Uint8Array(arrayBuffer);
    }

    return new Uint8Array(0);
}

/**
 * Log exception or error to all configured external error monitoring services.
 * @param error Exception
 * @param captureOptions Optional capture options object
 * @param attachment Optional attachment file - useful to attach excel files
 */
export const logToExternalErrorHandlers = (
    error: Error,
    captureOptions?: Partial<ScopeContext>,
    attachment?: SentryAttachment,
): void => {
    Sentry.withScope(async (scope) => {
        if (attachment) {
            const uint8Array = await getFileAsUint8Array(attachment.file);
            scope.addAttachment({
                data: uint8Array,
                filename: attachment.file.name,
                contentType: attachment.contentType,
                attachmentType: 'event.attachment',
            });
        }

        Sentry.captureException(error, getFinalCaptureOptions(error, captureOptions));
        scope.clearAttachments();
    });
};

export function getFinalCaptureOptions(error: Error, captureOptions?: Partial<ScopeContext>): Partial<ScopeContext> {
    const fingerprint = captureOptions?.fingerprint ?? getFingerprint(error);
    return {
        ...captureOptions,
        ...(fingerprint ? { fingerprint } : {}),
        extra: {
            'honeycomb-link': getHoneycombLink(error),
            'epibator-sha': getEpibatorSha(error),
            'luminovo-version': getLuminovoVersion(error),
            identifier: fingerprint,
            ...captureOptions?.extra,
        },
    };
}

/** http-client depends on this package, so its errors declare their grouping structurally, like getHoneycombLink. */
function getFingerprint(error: Error & { getSentryFingerprint?: unknown }): string[] | undefined {
    if (typeof error.getSentryFingerprint !== 'function') {
        return undefined;
    }
    const fingerprint: unknown = error.getSentryFingerprint();
    if (!Array.isArray(fingerprint) || fingerprint.some((part) => typeof part !== 'string')) {
        return undefined;
    }
    return fingerprint;
}

function getHoneycombLink(error: Error & { getHoneycombLink?: unknown }): string {
    if (!('getHoneycombLink' in error)) {
        return 'N/A';
    }
    if (typeof error.getHoneycombLink !== 'function') {
        return 'N/A';
    }
    return String(error.getHoneycombLink());
}

function getEpibatorSha(error: Error & { epibatorSha?: unknown }): string {
    if (!('epibatorSha' in error)) {
        return 'N/A';
    }
    return String(error.epibatorSha);
}

function getLuminovoVersion(error: Error & { luminovoVersion?: unknown }): string {
    if (!('luminovoVersion' in error)) {
        return 'N/A';
    }
    return String(error.luminovoVersion);
}
